/* "Pour chaque moment": full-screen tilted product slider.
   Motion model: centre-weighted spacing, sine-based size and brightness, velocity blur and lean,
   exponential smoothing, rotating circle text, staggered intro, and a fly-out page transition on click. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const COVER_MS = 760;
  const SWAY = [-0.053, 0.043, -0.024, 0.057, -0.048, 0.029, -0.038, 0.053];
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  const overlay = () => {
    let el = document.querySelector('.sk-page-transition');
    if (!el) {
      el = document.createElement('div');
      el.className = 'sk-page-transition';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<div class="sk-page-transition__panel"></div><div class="sk-page-transition__line"></div>';
      document.body.appendChild(el);
    }
    return el;
  };
  window.addEventListener('pageshow', () => {
    document.querySelector('.sk-page-transition')?.classList.remove('is-covering');
    document.querySelectorAll('[data-sk-moments].is-flying-out').forEach((s) => s.classList.remove('is-flying-out'));
    document.querySelectorAll('.sk-moment.is-chosen').forEach((c) => c.classList.remove('is-chosen'));
  });

  const init = (root) => {
    if (root.dataset.skMomentsReady) return;
    const wrap = root.querySelector('[data-sk-moments-band]');
    const track = root.querySelector('[data-sk-moments-track]');
    const originals = [...root.querySelectorAll('[data-sk-moment]')];
    if (!wrap || !track || !originals.length) return;
    root.dataset.skMomentsReady = 'true';

    const els = [...originals];
    let k = 0;
    while (els.length < 12) {
      const clone = originals[k % originals.length].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.removeAttribute('data-shopify-editor-block');
      track.appendChild(clone);
      els.push(clone);
      k++;
    }
    const items = els.map((element, i) => ({ element, i, hover: 0, hoverTarget: 0, delay: (i % 6) * 70 }));
    items.forEach((item) => {
      item.element.addEventListener('pointerenter', () => { item.hoverTarget = 1; });
      item.element.addEventListener('pointerleave', () => { item.hoverTarget = 0; });
    });
    const count = items.length;

    /* Cursor label on desktop. */
    const cursor = root.querySelector('[data-sk-moments-cursor]');

    let G = window.innerWidth;
    let itemW = 300;
    let itemH = 480;
    let N = 240;
    let F = N * count;
    const measure = () => {
      G = root.clientWidth || window.innerWidth;
      itemW = items[0].element.offsetWidth || 300;
      itemH = items[0].element.offsetHeight || 480;
      N = itemW * (G <= 480 ? 0.86 : 0.8);
      F = N * count;
    };

    let target = 0;
    let H = 0;
    let vel = 0;
    let dragging = false;
    let dragged = false;
    let downX = 0;
    let lastX = 0;
    let lastT = 0;
    let flick = 0;
    let lastScroll = window.scrollY;
    let visible = false;
    let running = false;
    let last = performance.now();
    let introStart = -1;

    const wrapX = (i, h) => {
      let x = (i * N + h + itemW) % F;
      if (x < 0) x += F;
      return x - itemW;
    };

    const render = (now, dt) => {
      const prev = H;
      if (reduce.matches) H = target;
      else {
        H += (target - H) * (1 - Math.exp(-(dragging ? 11 : (G <= 760 ? 1.6 : 2.2)) * dt));
        if (Math.abs(target - H) < 0.05) H = target;
      }
      vel = dt > 0 ? (H - prev) / dt : 0;
      const small = G <= 480;
      root.style.setProperty('--sk-turn', `${(H * (small ? -0.03 : -0.012)).toFixed(2)}deg`);

      const re = reduce.matches ? 0 : Math.min(1, Math.abs(vel) / 2400);
      const lean = -Math.sign(vel) * re * 6;
      const squash = 1 - re * re * 0.05;
      const speedBlur = re * re * (small ? 1.6 : 3.2);

      let introDone = true;
      const introBase = introStart < 0 ? -1 : now - introStart;
      root.style.setProperty('--sk-intro', introBase < 0 ? (reduce.matches ? 1 : 0) : easeOut(Math.min(1, introBase / 1150)).toFixed(3));

      items.forEach((item) => {
        const x = wrapX(item.i, H);
        const q = x / G;
        const warped = (q + 0.55 * 0.16 * Math.tanh((q - 0.5) / 0.16)) * G;
        const g = q >= 0 && q <= 1 ? Math.sin(q * Math.PI) : 0;
        const ye = Math.abs(q - 0.5) / (N / G);
        const scale = (0.55 + g * 0.8) * (1 - 0.1 * Math.min(1, ye));
        const bright = 0.7 + g * 0.4;
        const blur = Math.min(small ? 2 : 4, ye * ye) + speedBlur;
        const sway = reduce.matches ? 0 : Math.sin((item.i * N + H) / F * Math.PI * 2) * 18 * 0.35;
        const yOff = SWAY[item.i % SWAY.length] * itemH * 0.4;
        const R = Math.max(520, G * (G <= 760 ? 1.05 : 0.62));
        const theta = (warped - G / 2) / R;
        const cx = G / 2 + R * Math.sin(theta);
        const cy = R * (1 - Math.cos(theta));

        let p = 1;
        if (!reduce.matches) {
          if (introBase < 0) p = 0;
          else {
            p = easeOut(Math.min(1, Math.max(0, introBase - item.delay) / 1150));
            if (p < 1) introDone = false;
          }
        }
        item.hover += (item.hoverTarget - item.hover) * (reduce.matches ? 1 : Math.min(1, dt * 9));

        const s = scale * (0.9 + 0.1 * p) * squash * (1 + 0.05 * item.hover);
        const el = item.element;
        el.style.transform = `translate(${(cx - itemW / 2).toFixed(1)}px, ${(cy + yOff + (1 - p) * 56 - item.hover * 10).toFixed(1)}px) rotate(${(theta * 57.2958 + sway * 0.4 + lean).toFixed(2)}deg) scale(${s.toFixed(4)})`;
        el.style.opacity = p.toFixed(3);
        el.style.filter = `brightness(${bright.toFixed(3)}) blur(${blur.toFixed(2)}px)`;
        el.style.zIndex = String(Math.round(g * 100));
      });
      return introDone && Math.abs(target - H) < 0.05 && items.every((it) => Math.abs(it.hover - it.hoverTarget) < 0.002);
    };

    const frame = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const y = window.scrollY;
      target -= (y - lastScroll) * (G <= 760 ? 0.5 : 0.75);
      lastScroll = y;
      const settled = render(now, dt);
      if (visible && !(settled && !dragging && reduce.matches)) requestAnimationFrame(frame);
      else running = false;
    };
    const start = () => {
      if (running) return;
      running = true;
      last = performance.now();
      requestAnimationFrame(frame);
    };
    window.addEventListener('scroll', () => { if (visible) start(); }, { passive: true });

    /* Drag with a fling. */
    wrap.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      dragging = true;
      dragged = false;
      downX = lastX = e.clientX;
      lastT = e.timeStamp || performance.now();
      flick = 0;
      start();
    });
    wrap.addEventListener('pointermove', (e) => {
      if (cursor && finePointer.matches) {
        const r = root.getBoundingClientRect();
        cursor.style.transform = `translate(${e.clientX - r.left}px, ${e.clientY - r.top}px)`;
        root.classList.toggle('is-over-item', !!e.target.closest('[data-sk-moment]'));
      }
      if (!dragging) return;
      if (!dragged) {
        if (Math.abs(e.clientX - downX) <= 6) return;
        dragged = true;
        try { wrap.setPointerCapture(e.pointerId); } catch (err) {}
        wrap.classList.add('is-dragging');
        lastX = e.clientX;
        return;
      }
      const t = e.timeStamp || performance.now();
      const dx = e.clientX - lastX;
      flick = flick * 0.72 + (dx / Math.max(1, t - lastT)) * 0.28;
      lastX = e.clientX;
      lastT = t;
      target += dx * (G <= 760 ? 1 : 1.2);
    });
    const release = (e) => {
      if (!dragging) return;
      dragging = false;
      wrap.classList.remove('is-dragging');
      if (dragged) {
        try { wrap.releasePointerCapture(e.pointerId); } catch (err) {}
        target += Math.max(-3.5, Math.min(3.5, flick)) * (G <= 760 ? 1 * 120 : 1.2 * 170);
      }
      start();
    };
    wrap.addEventListener('pointerup', release);
    wrap.addEventListener('pointercancel', release);
    wrap.addEventListener('pointerleave', () => root.classList.remove('is-over-item'));

    /* Click: other cards fly out, a panel rises with a sweeping line, then the page changes. */
    wrap.addEventListener('click', (e) => {
      const card = e.target.closest('[data-sk-moment]');
      if (!card) return;
      if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; return; }
      if (e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      card.classList.add('is-chosen');
      root.classList.add('is-flying-out');
      const el = overlay();
      void el.offsetWidth;
      el.classList.add('is-covering');
      /* In the editor, play the transition as a preview and stay on the page. */
      if (window.Shopify?.designMode) {
        setTimeout(() => {
          el.classList.remove('is-covering');
          root.classList.remove('is-flying-out');
          card.classList.remove('is-chosen');
        }, COVER_MS + 500);
        return;
      }
      setTimeout(() => { window.location.href = card.getAttribute('href'); }, COVER_MS);
    }, true);

    /* Keyboard focus brings the card to the centre. */
    items.forEach((item) => item.element.addEventListener('focus', () => {
      const want = G / 2;
      const base = want - item.i * N;
      target = base + Math.round((H - base) / F) * F;
      start();
    }));

    new IntersectionObserver((entries) => {
      visible = entries.some((e) => e.isIntersecting);
      if (visible) {
        if (introStart < 0) { introStart = performance.now(); root.classList.add('is-in'); }
        lastScroll = window.scrollY;
        start();
      }
    }, { threshold: 0.15 }).observe(root);
    window.addEventListener('resize', () => { measure(); render(performance.now(), 0); }, { passive: true });

    measure();
    target = H = G / 2 - N;
    render(performance.now(), 0);
    root.classList.add('is-ready');
  };

  const boot = () => document.querySelectorAll('[data-sk-moments]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
